import { useEffect, useMemo, useState, type FC } from 'react';
import type { ColDef } from 'ag-grid-community';
import { AgGridReact, type CustomCellRendererProps } from 'ag-grid-react';

import { ButtonField, Smokebreak } from '@wk/components-react16';
import { IconLoader2 } from '@tabler/icons-react';

import {
  useSiteComplianceAssessmentQuery,
} from '@/domains/siteCompliance';
import type {
  Evaluation,
  EvaluationCheckpoint,
} from '@/domains/evaluation/evaluation.types';
import { exportToCsv, isJSON, isUrl } from '@/lib/utils';
import useToast from '@/shared/stores/toastStore';

import './AssessmentDrawer.css';

interface AssessmentDrawerProps {
  checkpoint: EvaluationCheckpoint;
  evaluation: Evaluation | null;
  isOpen: boolean;
  onClose: () => void;
  title?: string;
}

const toRows = (
  rows: Record<string, unknown>[] | undefined,
  fallbackRows: Record<string, unknown>[],
) => {
  if (rows?.length) {
    return rows;
  }

  return fallbackRows;
};

const AssessmentDrawer: FC<AssessmentDrawerProps> = ({
  checkpoint,
  evaluation,
  isOpen,
  onClose,
  title = 'Checkpoint Assessment Data',
}) => {
  const { showToast } = useToast();
  const [isDownloadingCsv, setIsDownloadingCsv] = useState(false);
  const {
    data: assessmentData,
    error,
    isFetching,
    isLoading,
  } = useSiteComplianceAssessmentQuery(
    evaluation?.id,
    checkpoint.metadata.id,
    {
      enabled: isOpen,
    },
  );
  const fallbackRows = useMemo(
    () => checkpoint.assessmentData as Record<string, unknown>[],
    [checkpoint.assessmentData],
  );
  const rows = useMemo(
    () => toRows(assessmentData?.rows, fallbackRows),
    [assessmentData?.rows, fallbackRows],
  );
  const rawJson = useMemo(() => {
    if (rows.length > 0) {
      return null;
    }

    try {
      return JSON.stringify(assessmentData?.raw ?? fallbackRows, null, 2);
    } catch {
      return String(assessmentData?.raw ?? '');
    }
  }, [assessmentData?.raw, fallbackRows, rows.length]);
  const columnDefs = useMemo<ColDef<Record<string, unknown>>[]>(() => {
    const columns = rows.reduce<string[]>((acc, row) => {
      Object.keys(row).forEach((key) => {
        if (!acc.includes(key)) {
          acc.push(key);
        }
      });

      return acc;
    }, []);

    return columns.map((key) => ({
      field: key,
      headerName: key,
      wrapText: true,
      cellRenderer: (params: CustomCellRendererProps<Record<string, unknown>>) => {
        const value = params.value;

        if (value === null || value === undefined) {
          return <span>N/A</span>;
        }

        if (typeof value === 'object') {
          return (
            <pre className="whitespace-pre-wrap break-all font-mono text-xs">
              {JSON.stringify(value, null, 2)}
            </pre>
          );
        }

        if (typeof value === 'string') {
          if (isUrl(value)) {
            return (
              <a
                className="text-blue-500 underline"
                href={value}
                rel="noopener noreferrer"
                target="_blank"
              >
                {value}
              </a>
            );
          }

          if (isJSON(value)) {
            try {
              return (
                <pre className="whitespace-pre-wrap break-all font-mono text-xs">
                  {JSON.stringify(JSON.parse(value), null, 2)}
                </pre>
              );
            } catch {
              // fall through
            }
          }
        }

        return <span>{String(value)}</span>;
      },
    }));
  }, [rows]);

  useEffect(() => {
    if (!error) {
      return;
    }

    console.error('Site Compliance assessment data fetch failed', {
      checkpointId: checkpoint.metadata.id,
      error,
      evaluationId: evaluation?.id ?? null,
      jobId: null,
      siteId: evaluation?.metadata.bitId ?? evaluation?.bitId ?? null,
    });

    if (!fallbackRows.length) {
      showToast('error', {
        title: 'Error loading assessment data',
        message: 'Assessment data could not be loaded for this checkpoint.',
      });
    }
  }, [
    checkpoint.metadata.id,
    error,
    evaluation?.bitId,
    evaluation?.id,
    evaluation?.metadata.bitId,
    fallbackRows.length,
    showToast,
  ]);

  const handleExport = async () => {
    setIsDownloadingCsv(true);

    try {
      await exportToCsv({
        fileName: `${evaluation?.id ?? 'evaluation'}-${checkpoint.metadata.id}-assessment-data`,
        options: {},
        rows,
      });
    } finally {
      setIsDownloadingCsv(false);
    }
  };

  const isBusy = isLoading || isFetching;

  return (
    <div className="sc-assessment-drawer">
      <Smokebreak isOpen={isOpen} position="right" onUserRequest={onClose}>
        <h2 slot="smokebreakHeader" className="p-3 text-lg font-semibold">
          {title}
        </h2>
      <div slot="smokebreakBody" className="flex h-full w-full flex-col gap-3 p-3">
        <div className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
          <div>
            <p className="text-xs font-medium text-gray-500">Checkpoint</p>
            <p className="font-semibold text-gray-900">
              {checkpoint.metadata.id}
            </p>
          </div>
          <div>
            <p className="text-xs font-medium text-gray-500">Status</p>
            <p className="font-semibold text-gray-900">
              {assessmentData?.status ?? checkpoint.status}
            </p>
          </div>
        </div>

        {rows.length > 0 ? (
          <div className="flex justify-end">
            <ButtonField>
              <button
                type="button"
                disabled={isDownloadingCsv}
                onClick={handleExport}
              >
                Export to CSV
                {isDownloadingCsv ? (
                  <IconLoader2 className="animate-spin" size={16} />
                ) : null}
              </button>
            </ButtonField>
          </div>
        ) : null}

        {isBusy ? (
          <div className="flex min-h-[220px] flex-1 items-center justify-center">
            <IconLoader2 className="animate-spin" size={28} />
          </div>
        ) : rows.length > 0 ? (
          <div className="min-h-0 flex-1">
            <div className="cg-ag-grid-field ag-theme-balham h-full w-full">
              <AgGridReact
                autoSizeStrategy={{
                  defaultMinWidth: 220,
                  type: 'fitGridWidth',
                }}
                columnDefs={columnDefs}
                defaultColDef={{
                  autoHeight: true,
                  wrapText: true,
                }}
                enableCellTextSelection
                rowData={rows}
              />
            </div>
          </div>
        ) : (
          <pre className="min-h-[220px] overflow-auto rounded border border-gray-200 bg-gray-50 p-3 text-xs text-gray-700">
            {rawJson || 'No assessment data available.'}
          </pre>
        )}
      </div>
      </Smokebreak>
    </div>
  );
};

export default AssessmentDrawer;
